import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, Post } from '../utils/api';
import { useLanguage } from '../contexts/LanguageContext';

const categoryColors: Record<string, { bg: string; color: string; circleBg: string }> = {
  gala: { bg: 'var(--red-bg)', color: 'var(--red-primary)', circleBg: 'var(--red-primary)' },
  workshop: { bg: 'var(--yellow-bg)', color: 'var(--yellow-accent)', circleBg: 'var(--yellow-accent)' },
  community: { bg: 'var(--green-bg)', color: 'var(--green-accent)', circleBg: 'var(--green-accent)' },
  education: { bg: 'var(--blue-bg)', color: 'var(--blue-accent)', circleBg: 'var(--blue-accent)' },
  seminar: { bg: 'var(--blue-bg)', color: 'var(--blue-accent)', circleBg: 'var(--blue-accent)' },
  culture: { bg: 'var(--orange-bg)', color: 'var(--orange-accent)', circleBg: 'var(--orange-accent)' },
  advocacy: { bg: 'var(--pink-bg)', color: 'var(--pink-accent)', circleBg: 'var(--pink-accent)' },
};

const fallbackEvents: Post[] = [
  {
    id: 10, title: 'Festive Feast 2026', subtitle: 'Annual Community Gala', slug: 'festive-feast-2026',
    content: 'Join us for CAAOC\'s signature annual gala at Irvine Barclay Theatre with live entertainment and cultural performances.',
    post_type: 'event', status: 'published', featured: 1,
    event_date: '2026-01-30', event_time: '5:30 PM - 9:30 PM', event_location: 'Irvine Barclay Theatre, 4242 Campus Dr, Irvine',
    category: 'gala', author_id: 1, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 11, title: 'Festive Feast 2025', subtitle: 'Year of the Snake Gala', slug: 'festive-feast-2025',
    content: 'Annual Lunar New Year Spring Gala at Irvine Barclay Theatre.',
    post_type: 'event', status: 'published', featured: 0,
    event_date: '2025-01-12', event_time: '5:30 PM - 9:30 PM', event_location: 'Irvine Barclay Theatre',
    category: 'gala', author_id: 1, created_at: '2025-01-01T00:00:00Z', updated_at: '2025-01-01T00:00:00Z',
  },
  {
    id: 12, title: 'Y.E.T. Youth Training', subtitle: 'Youth Effectiveness Training', slug: 'yet',
    content: '18-hour social-emotional skills workshop for ages 13-20.',
    post_type: 'event', status: 'published', featured: 0,
    event_date: '2026-03-29', event_time: '9:00 AM - 5:00 PM', event_location: '2040 Main Street, Irvine',
    category: 'workshop', author_id: 1, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z',
  },
  {
    id: 13, title: 'Parent Workshop Series', subtitle: 'Monthly Education', slug: 'parent-workshop',
    content: 'Monthly workshops covering education, parenting, and community resources.',
    post_type: 'event', status: 'published', featured: 0,
    event_date: '2026-04-15', event_time: '10:00 AM - 12:00 PM', event_location: 'Irvine Community Center',
    category: 'education', author_id: 1, created_at: '2026-01-01T00:00:00Z', updated_at: '2026-01-01T00:00:00Z',
  },
];

export default function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { locale, t, prefix } = useLanguage();
  const isZh = locale === 'zh';
  const [event, setEvent] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get<{ posts: Post[] }>(`/posts?post_type=event&status=published&limit=50`)
      .then((data) => {
        if (data.posts) {
          const found = data.posts.find((p) => p.slug === slug);
          if (found) {
            setEvent(found);
            return;
          }
        }
        // Fallback
        const fallback = fallbackEvents.find((p) => p.slug === slug);
        if (fallback) setEvent(fallback);
      })
      .catch(() => {
        const fallback = fallbackEvents.find((p) => p.slug === slug);
        if (fallback) setEvent(fallback);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!event) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 24px 80px' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: 16 }}>
          {isZh ? '活动未找到' : 'Event Not Found'}
        </h1>
        <p style={{ color: 'var(--gray-500)', marginBottom: 24 }}>
          {isZh ? '该活动不存在或已被移除。' : 'This event does not exist or has been removed.'}
        </p>
        <Link to={prefix + '/events'} className="btn btn-primary">
          {isZh ? '返回活动列表' : 'Back to Events'}
        </Link>
      </div>
    );
  }

  const catStyle = categoryColors[event.category || ''] || categoryColors['community'];
  const isPast = event.event_date ? event.event_date < new Date().toISOString().split('T')[0] : false;

  return (
    <div>
      {/* Hero */}
      <section
        style={{
          background: `linear-gradient(135deg, ${catStyle.circleBg} 0%, ${catStyle.circleBg}dd 100%)`,
          color: '#fff',
          padding: '100px 0 60px',
          position: 'relative',
        }}
      >
        <div className="container">
          <Link
            to={prefix + '/events'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'rgba(255,255,255,0.8)',
              fontSize: '0.9rem',
              marginBottom: 20,
            }}
          >
            &#8592; {isZh ? '返回活动列表' : 'Back to Events'}
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            {event.category && (
              <span
                className="badge"
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontSize: '0.85rem',
                  padding: '6px 16px',
                }}
              >
                {event.category}
              </span>
            )}
            {isPast && (
              <span
                className="badge"
                style={{
                  background: 'rgba(0,0,0,0.2)',
                  color: '#fff',
                  fontSize: '0.85rem',
                  padding: '6px 16px',
                }}
              >
                {isZh ? '已结束' : 'Past Event'}
              </span>
            )}
          </div>
          <h1 style={{ fontSize: '2.8rem', fontWeight: 900, marginBottom: 8, lineHeight: 1.1 }}>
            {event.title}
          </h1>
          {event.subtitle && (
            <p style={{ fontSize: '1.3rem', fontWeight: 300, opacity: 0.9 }}>
              {event.subtitle}
            </p>
          )}
        </div>
      </section>

      {/* Main content */}
      <section style={{ padding: 'var(--section-padding)', background: 'var(--white)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 48, alignItems: 'start' }}>
            {/* Left: content */}
            <div>
              {event.featured_image_url && (
                <img
                  src={event.featured_image_url}
                  alt={event.title}
                  style={{
                    width: '100%',
                    maxHeight: 400,
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-md)',
                    marginBottom: 32,
                  }}
                />
              )}

              <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: 16, color: 'var(--gray-900)' }}>
                {isZh ? '活动介绍' : 'About This Event'}
              </h2>
              <div
                style={{ fontSize: '1.05rem', color: 'var(--gray-600)', lineHeight: 1.8 }}
                dangerouslySetInnerHTML={{ __html: event.content || '' }}
              />

              {/* Video */}
              {event.video_url && (
                <div style={{ marginTop: 40 }}>
                  <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 16 }}>
                    {isZh ? '视频' : 'Video'}
                  </h3>
                  <div
                    style={{
                      position: 'relative',
                      paddingBottom: '56.25%',
                      height: 0,
                      borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                      background: '#000',
                    }}
                  >
                    <iframe
                      src={event.video_url.replace('watch?v=', 'embed/')}
                      title={event.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Right: sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              {/* Details card */}
              <div
                className="card"
                style={{
                  borderTop: `4px solid ${catStyle.circleBg}`,
                  padding: 28,
                }}
              >
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: 20 }}>
                  {isZh ? '活动详情' : 'Event Details'}
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {event.event_date && (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>&#128197;</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {isZh ? '日期' : 'Date'}
                        </div>
                        <div style={{ fontWeight: 600, color: 'var(--gray-800)' }}>
                          {new Date(event.event_date + 'T00:00:00').toLocaleDateString(
                            isZh ? 'zh-CN' : 'en-US',
                            { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {event.event_time && (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>&#9200;</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {isZh ? '时间' : 'Time'}
                        </div>
                        <div style={{ fontWeight: 600, color: 'var(--gray-800)' }}>{event.event_time}</div>
                      </div>
                    </div>
                  )}
                  {event.event_location && (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>&#128205;</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {isZh ? '地点' : 'Location'}
                        </div>
                        <div style={{ fontWeight: 600, color: 'var(--gray-800)' }}>{event.event_location}</div>
                      </div>
                    </div>
                  )}
                  {event.category && (
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>&#127991;</span>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          {isZh ? '类别' : 'Category'}
                        </div>
                        <Link
                          to={`${prefix}/events?category=${event.category}`}
                          className="badge"
                          style={{ background: catStyle.bg, color: catStyle.color, textDecoration: 'none' }}
                        >
                          {event.category}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {event.ticket_url && !isPast && (
                  <a
                    href={event.ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ justifyContent: 'center', padding: '14px 24px', fontSize: '1rem' }}
                  >
                    {event.button1_text || (isZh ? '获取门票' : 'Get Tickets')}
                  </a>
                )}
                {event.button1_url && !event.ticket_url && (
                  <a
                    href={event.button1_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ justifyContent: 'center', padding: '14px 24px', fontSize: '1rem' }}
                  >
                    {event.button1_text || (isZh ? '了解更多' : 'Learn More')}
                  </a>
                )}
                {event.button2_url && (
                  <a
                    href={event.button2_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline"
                    style={{ justifyContent: 'center', padding: '14px 24px', fontSize: '1rem' }}
                  >
                    {event.button2_text || (isZh ? '更多信息' : 'More Info')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 768px) {
          .container > div[style*="grid-template-columns: 2fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}
